// Resolve a `session.failed` payload to a user-facing message. Known failures
// carry a stable `code` (set by the backend) so we show a localized, actionable
// hint; unknown failures collapse to a generic message. We never surface the raw
// upstream/gateway text, which leaks the API key and dumps codex stderr at the
// user. Shared by the cloud (AgentScreen) and local-device (LocalAgentScreen)
// chat views so both stay consistent.
const DEFAULT_MIN_RUN_QUOTA_REMAINING_LABEL = "$5";

export interface FailurePayload {
  message?: string;
  code?: string;
  minimumRunQuotaRemainingUsd?: number;
  /** missing_deliverable: helper files produced instead of the requested document. */
  processFiles?: string[];
  /** missing_deliverable: requested formats that were not produced while others were (e.g. ["PDF"]). */
  missingFormats?: string[];
}

export function terminalFailureEvent<T extends { type: string }>(sessionStatus: string, events: T[]): T | undefined {
  if (sessionStatus !== "failed") return undefined;
  return events.find((event) => event.type === "session.failed");
}

function quotaReserveLabel(payload: { minimumRunQuotaRemainingUsd?: number } | undefined): string {
  const value = payload?.minimumRunQuotaRemainingUsd;
  if (typeof value !== "number" || !Number.isFinite(value)) return DEFAULT_MIN_RUN_QUOTA_REMAINING_LABEL;
  return `$${value.toFixed(value % 1 === 0 ? 0 : 2)}`;
}

export function failureText(
  payload: FailurePayload | undefined,
  t: (k: string, vars?: Record<string, string | number>) => string
): string | null {
  if (!payload) return null;
  if (payload.code === "usage_quota_exceeded") return t("agent.quotaExceeded");
  if (payload.code === "usage_quota_low") return t("agent.quotaLow", { minimum: quotaReserveLabel(payload) });
  if (payload.code === "context_length_exceeded") return t("agent.contextTooLarge");
  if (payload.code === "rate_limited") return t("agent.rateLimited");
  if (payload.code === "credential_rejected") return t("agent.credentialRejected");
  if (payload.code === "agent_timeout") return t("agent.timeout");
  if (payload.code === "agent_interrupted") return t("agent.interrupted");
  if (payload.code === "missing_deliverable") {
    const missing = Array.isArray(payload.missingFormats) ? payload.missingFormats.filter(Boolean) : [];
    if (missing.length > 0) return t("agent.missingFormats", { formats: missing.join("、") });
    const helpers = Array.isArray(payload.processFiles) ? payload.processFiles.filter(Boolean) : [];
    return helpers.length > 0
      ? t("agent.missingDeliverableProcessOnly", { files: helpers.slice(0, 3).join("、") + (helpers.length > 3 ? " …" : "") })
      : t("agent.missingDeliverable");
  }
  // Backwards/safety net: classify by message text for older or unstamped payloads.
  const msg = payload.message ?? "";
  if (/invalid_api_key|incorrect api key|credential rejected|model credential rejected|authentication failed/i.test(msg)) {
    return t("agent.credentialRejected");
  }
  if (/quota (?:is )?too low|remaining quota|low balance/i.test(msg)) {
    return t("agent.quotaLow", { minimum: quotaReserveLabel(payload) });
  }
  if (/quota|usage limit|usage has reach|token usage|token limit|tokens? limit|insufficient credits/i.test(msg)) {
    return t("agent.quotaExceeded");
  }
  if (/too many tokens|max(?:imum)? tokens?|context length|context window|maximum context/i.test(msg)) {
    return t("agent.contextTooLarge");
  }
  if (/\b429\b|too many requests|exceeded retry limit|rate.?limit/i.test(msg)) return t("agent.rateLimited");
  if (/timed out|timeout/i.test(msg)) return t("agent.timeout");
  if (/interrupted/i.test(msg)) return t("agent.interrupted");
  // Never surface raw gateway / codex stderr to the user; show a generic hint.
  if (payload.code === "agent_error" || /gateway returned|codex stderr/i.test(msg)) return t("agent.error");
  return msg || t("agent.error");
}

export function failedSessionText(
  existingText: string,
  payload: FailurePayload | undefined,
  t: (k: string, vars?: Record<string, string | number>) => string
): string {
  const message = failureText(payload, t) ?? t("agent.error");
  return payload?.code === "missing_deliverable" ? `⚠ ${message}` : existingText || `⚠ ${message}`;
}
